import { useState, useCallback, useRef } from 'react';
import { api } from '../../services/api';
import { FileDropzone } from './FileDropzone';
import type { ParsedChapter } from '../../services/types';

interface AddChaptersModalProps {
  novelId: string;
  existingChapterCount: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Stage = 'upload' | 'preview' | 'saving';

interface FileContent {
  filename: string;
  content: string;
}

interface UploadProgress {
  current: number;
  total: number;
  currentChapterTitle: string;
}

export function AddChaptersModal({
  novelId,
  existingChapterCount,
  isOpen,
  onClose,
  onSuccess,
}: AddChaptersModalProps) {
  const [stage, setStage] = useState<Stage>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const abortRef = useRef(false);

  // 檔案內容快取
  const [fileContents, setFileContents] = useState<FileContent[]>([]);

  // 解析結果
  const [chapters, setChapters] = useState<ParsedChapter[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  // 處理檔案上傳
  const handleFilesReady = useCallback(async (files: File[]) => {
    setIsLoading(true);
    setError(null);

    try {
      // 讀取所有檔案內容
      const contents: FileContent[] = [];
      for (const file of files) {
        const content = await file.text();
        contents.push({
          filename: file.name,
          content
        });
      }
      setFileContents(contents);

      // 呼叫 API 解析
      const result = await api.parseNovelFiles(files);

      // 調整章節序號（從現有章節數 + 1 開始）
      const adjustedChapters = result.chapters.map((ch, idx) => ({
        ...ch,
        suggestedIndex: existingChapterCount + idx + 1
      }));

      setChapters(adjustedChapters);
      setWarnings(result.warnings);
      setStage('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : '解析檔案時發生錯誤');
    } finally {
      setIsLoading(false);
    }
  }, [existingChapterCount]);

  // 更新章節標題
  const handleTitleChange = useCallback((index: number, title: string) => {
    setChapters(prev => {
      const newChapters = [...prev];
      newChapters[index] = { ...newChapters[index], suggestedTitle: title };
      return newChapters;
    });
  }, []);

  // 移除章節
  const handleRemove = useCallback((index: number) => {
    setChapters(prev => {
      const newChapters = prev.filter((_, i) => i !== index);
      // 重新編號
      newChapters.forEach((ch, i) => {
        ch.suggestedIndex = existingChapterCount + i + 1;
      });
      return newChapters;
    });
    setFileContents(prev => prev.filter((_, i) => i !== index));
  }, [existingChapterCount]);

  // 重新上傳
  const handleReset = useCallback(() => {
    setStage('upload');
    setFileContents([]);
    setChapters([]);
    setWarnings([]);
    setError(null);
    setUploadProgress(null);
    abortRef.current = false;
  }, []);

  // 確認上傳
  const handleUpload = useCallback(async () => {
    if (chapters.length === 0) {
      setError('請至少保留一個章節');
      return;
    }

    setStage('saving');
    setIsLoading(true);
    setError(null);
    abortRef.current = false;

    try {
      for (let i = 0; i < chapters.length; i++) {
        if (abortRef.current) {
          throw new Error('上傳已取消');
        }

        const chapter = chapters[i];
        const fileContent = fileContents.find(
          fc => fc.filename === chapter.originalFilename
        );

        setUploadProgress({
          current: i + 1,
          total: chapters.length,
          currentChapterTitle: chapter.suggestedTitle,
        });

        await api.uploadChapter(novelId, {
          index: chapter.suggestedIndex,
          title: chapter.suggestedTitle,
          content: fileContent?.content || ''
        });
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : '上傳失敗');
      setStage('preview');
    } finally {
      setIsLoading(false);
      setUploadProgress(null);
    }
  }, [chapters, fileContents, novelId, onSuccess]);

  // 取消上傳
  const handleCancelUpload = useCallback(() => {
    abortRef.current = true;
  }, []);

  // 計算總字數
  const totalWordCount = chapters.reduce((sum, ch) => sum + ch.wordCount, 0);

  if (!isOpen) return null;

  return (
    <div className="modal">
      {/* Backdrop */}
      <div className="overlay" onClick={stage !== 'saving' ? onClose : undefined} />

      {/* Modal */}
      <div className="modal-content max-w-3xl">
        {/* Header */}
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-button bg-primary-500 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div>
              <h3 className="heading-section">新增章節</h3>
              <p className="body-small">上傳新章節到現有小說</p>
            </div>
          </div>

          {stage !== 'saving' && (
            <button onClick={onClose} className="btn btn-ghost btn-icon">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mx-5 mb-4 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-button flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Content */}
        <div className="card-body">
          {/* Stage: Upload */}
          {stage === 'upload' && (
            <FileDropzone onFilesReady={handleFilesReady} isLoading={isLoading} />
          )}

          {/* Stage: Preview */}
          {stage === 'preview' && !isLoading && (
            <div className="space-y-4">
              {/* 警告訊息 */}
              {warnings.length > 0 && (
                <div className="p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800/50 text-warning-700 dark:text-warning-300 rounded-button">
                  <div className="font-medium mb-1 text-sm">解析警告:</div>
                  <ul className="list-disc list-inside text-xs">
                    {warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 統計資訊 */}
              <div className="flex items-center justify-between body-small">
                <span>將新增 {chapters.length} 個章節</span>
                <span>總字數: {totalWordCount.toLocaleString()} 字</span>
              </div>

              {/* 章節列表 */}
              <div className="border border-neutral-200 dark:border-neutral-700 rounded-card max-h-80 overflow-y-auto">
                {chapters.map((chapter, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 dark:border-neutral-700/50 last:border-b-0"
                  >
                    {/* 序號 */}
                    <span className="flex-shrink-0 w-12 text-center badge badge-neutral">
                      #{chapter.suggestedIndex}
                    </span>

                    {/* 標題輸入 */}
                    <input
                      type="text"
                      value={chapter.suggestedTitle}
                      onChange={(e) => handleTitleChange(index, e.target.value)}
                      className="input flex-1"
                      placeholder="章節標題"
                    />

                    {/* 字數 */}
                    <span className="flex-shrink-0 body-small w-20 text-right">
                      {chapter.wordCount.toLocaleString()} 字
                    </span>

                    {/* 移除按鈕 */}
                    <button
                      onClick={() => handleRemove(index)}
                      className="btn btn-ghost btn-icon text-danger-500 hover:text-danger-600"
                      title="移除此章節"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* 重新選擇檔案 */}
              <button
                onClick={handleReset}
                className="btn btn-ghost btn-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                重新選擇檔案
              </button>
            </div>
          )}

          {/* Stage: Saving */}
          {stage === 'saving' && uploadProgress && (
            <div className="space-y-4 py-4">
              <h3 className="heading-card text-center">正在上傳章節...</h3>

              {/* 進度條 */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                  <span>第 {uploadProgress.current} / {uploadProgress.total} 章</span>
                  <span>{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
                </div>
                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-primary-500 h-full transition-all duration-300 ease-out"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>

              {/* 目前章節 */}
              {uploadProgress.currentChapterTitle && (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate text-center">
                  {uploadProgress.currentChapterTitle}
                </p>
              )}

              {/* 取消按鈕 */}
              <div className="text-center">
                <button
                  onClick={handleCancelUpload}
                  className="btn btn-ghost btn-sm text-danger-600"
                >
                  取消上傳
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {stage === 'preview' && (
          <div className="card-footer flex items-center justify-end gap-3">
            <button onClick={onClose} className="btn btn-ghost btn-md">
              取消
            </button>
            <button
              onClick={handleUpload}
              disabled={chapters.length === 0}
              className="btn btn-primary btn-md"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              上傳 {chapters.length} 個章節
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
